import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { X, Mail, Lock } from 'lucide-react';
import { Dialog, DialogContent } from '../ui/dialog';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import FormInput from './FormInput';
import SocialAuthButtons from './SocialAuthButtons';
import { loginSchema } from '../../schemas/authSchemas';
import { useAuthContext } from '../../context/AuthContext';
import toast from 'react-hot-toast';

const LoginModal = ({ isOpen, onClose, onSwitchToRegister, onSwitchToForgotPassword }) => {
  const { login, isLoading } = useAuthContext();
  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    watch
  } = useForm({
    resolver: zodResolver(loginSchema),
    mode: 'onChange'
  });

  const onSubmit = async (data) => {
    const result = await login(data.email, data.password);
    
    if (result.success) {
      toast.success('¡Bienvenido de nuevo!', {
        icon: '✅',
        duration: 3000
      });
      onClose();
    } else {
      toast.error('Email o contraseña incorrectos', {
        icon: '❌',
        duration: 3000
      });
    }
  };

  const handleSocialLogin = (provider) => {
    toast.success(`Iniciando sesión con ${provider}...`);
    // Aquí iría la lógica de OAuth
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.2 }}
        >
          {/* Header */}
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold mb-2">Iniciar sesión</h2>
            <p className="text-muted-foreground">¡Bienvenido de nuevo a La Orda!</p>
          </div>

          {/* Social Login */}
          <SocialAuthButtons
            onGoogleLogin={() => handleSocialLogin('Google')}
            onFacebookLogin={() => handleSocialLogin('Facebook')}
          />

          {/* Divider */}
          <div className="flex items-center gap-4 my-6">
            <div className="flex-1 h-px bg-border"></div>
            <span className="text-muted-foreground text-sm">O continúa con email</span>
            <div className="flex-1 h-px bg-border"></div>
          </div>

          {/* Login Form */}
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <FormInput
              label="Email"
              name="email"
              type="email"
              placeholder="tu@email.com"
              icon={<Mail className="w-5 h-5" />}
              error={errors.email?.message}
              required
              register={register}
            />

            <FormInput
              label="Contraseña"
              name="password"
              type="password"
              placeholder="••••••••"
              icon={<Lock className="w-5 h-5" />}
              error={errors.password?.message}
              required
              register={register}
            />

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Checkbox id="remember" {...register('rememberMe')} />
                <label htmlFor="remember" className="text-sm text-muted-foreground cursor-pointer">
                  Mantener sesión iniciada
                </label>
              </div>
              <button
                type="button"
                onClick={onSwitchToForgotPassword}
                className="text-sm text-primary hover:underline"
              >
                ¿Olvidaste tu contraseña?
              </button>
            </div>

            <Button
              type="submit"
              className="w-full"
              size="lg"
              disabled={isLoading || !isValid}
            >
              {isLoading ? 'Iniciando sesión...' : 'Iniciar sesión'}
            </Button>
          </form>

          {/* Footer */}
          <p className="text-center text-sm text-muted-foreground mt-6">
            ¿No tienes cuenta?{' '}
            <button
              onClick={onSwitchToRegister}
              className="text-primary hover:underline font-medium"
            >
              Regístrate aquí
            </button>
          </p>
        </motion.div>
      </DialogContent>
    </Dialog>
  );
};

export default LoginModal;